import type { ApplySuccessPageProps } from '@app/p/apply-success/[token]/[step]/page'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { CircularTimer } from '@components/primitives/CircularProgressRing/CircularTimer'
import { Icon } from '@components/primitives/Icon'
import Modal from '@components/primitives/Modal/index.client'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight'
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe'
import { faPhone as faPhoneRegular } from '@fortawesome/pro-regular-svg-icons/faPhone'
import { faVolume } from '@fortawesome/pro-regular-svg-icons/faVolume'
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons/faArrowDown'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faHandPointUp } from '@fortawesome/pro-solid-svg-icons/faHandPointUp'
import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone'
import { trackEvent } from '@lib/tracking'
import { isInAppBrowser, isIos } from '@lib/tracking/isInAppBrowser'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { getApplySuccessRouteToRedirectTo } from '../getApplySuccessRouteToRedirectTo'
import ClinicLocationHeader from './ClinicLocationHeader'

type PatientCallToSiteIntroProps = ApplySuccessPageProps & {
  handleNextButtonClick: () => void
  microphoneAccessGranted: boolean
  onStartCall: () => void
  setMicrophoneAccessGranted: (granted: boolean) => void
}

export function PatientCallToSiteIntro(props: PatientCallToSiteIntroProps) {
  const {
    clinicName,
    idToken,
    onStartCall,
    patientUserAgent,
    setMicrophoneAccessGranted,
    step,
    urlToExitInAppBrowserForPatientCallToSite,
  } = props

  const [callSiteModalOpen, setCallSiteModalOpen] = useState(false)
  const [inAppBrowserModalOpen, setInAppBrowserModalOpen] = useState(false)
  const [micModalOpen, setMicModalOpen] = useState(false)
  const [telUriClicked, setTelUriClicked] = useState(false)
  const router = useRouter()

  const requestMicrophoneAccess = async () => {
    try {
      if (isInAppBrowser()) {
        setInAppBrowserModalOpen(true)
        setMicModalOpen(false)
      } else {
        trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_MICROPHONE_REQUESTED, {
          trialApplicationIdToken: idToken,
        })
        setMicModalOpen(true)
        await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_MICROPHONE_GRANTED, {
          trialApplicationIdToken: idToken,
        })
        setMicrophoneAccessGranted(true)
        setMicModalOpen(false)
        onStartCall()
      }
    } catch (error: any) {
      // Error types: NotAllowedError, NotFoundError, NotReadableError, etc
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_MICROPHONE_DENIED, {
        trialApplicationIdToken: idToken,
        ...(error.name ? { val: error.name } : {}), // Send error type to segment
      })
      router.push(getApplySuccessRouteToRedirectTo(step, props))
    }
  }

  const exitInAppBrowser = () => {
    // This does not mean the user exited because the in-app browser would have its own modal to confirm after ours
    trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_IN_APP_BROWSER_EXITED, {
      trialApplicationIdToken: idToken,
    })
    window.open(
      urlToExitInAppBrowserForPatientCallToSite,
      '_blank',
      'noopener noreferrer',
    )
  }

  const handleTelUriClick = () => {
    trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_TEL_URI_CLICKED, {
      trialApplicationIdToken: idToken,
    })
    setCallSiteModalOpen(true)
    setTelUriClicked(true)
  }

  return (
    <div className='flex h-full flex-col'>
      <MicrophonePermissionModal
        isOpen={micModalOpen}
        onClose={setMicModalOpen}
      />
      <ChangeBrowserModal
        isOpen={inAppBrowserModalOpen}
        onClick={exitInAppBrowser}
        onClose={setInAppBrowserModalOpen}
        userAgent={patientUserAgent}
      />
      <DirectCallSiteOnMobileModal
        clinicName={clinicName}
        isOpen={callSiteModalOpen}
        onClose={setCallSiteModalOpen}
      />
      <div className='fixed inset-0 -z-10 bg-gradient-to-b from-white via-white to-primary50' />
      <DesktopView
        {...props}
        handleTelUriClick={handleTelUriClick}
        requestMicrophoneAccess={requestMicrophoneAccess}
        telUriClicked={telUriClicked}
      />
      <MobileView
        {...props}
        handleTelUriClick={handleTelUriClick}
        requestMicrophoneAccess={requestMicrophoneAccess}
        telUriClicked={telUriClicked}
      />
    </div>
  )
}

type PatientCallToSiteIntroVariantProps = PatientCallToSiteIntroProps & {
  handleTelUriClick: () => void
  requestMicrophoneAccess: () => void
  telUriClicked: boolean
}

const DesktopView = (props: PatientCallToSiteIntroVariantProps) => {
  const {
    clinicName,
    conditionName,
    handleNextButtonClick,
    idToken,
    microphoneAccessGranted,
    mostQualifiedTrialApplicationIdToken,
    onStartCall,
    patientName,
    requestMicrophoneAccess,
    telUriClicked,
    trialLocationImageId,
    usePlainPatientCallToSiteTreatment,
  } = props

  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  return (
    <div className='hidden md:block'>
      <div
        className={classMerge(
          'flex max-w-4xl flex-col items-center gap-16 md:mx-auto md:mt-4 md:flex-row',
          usePlainPatientCallToSiteTreatment
            ? 'justify-center'
            : 'justify-between',
        )}
      >
        <div className='flex w-full flex-col gap-4 md:w-2/3 md:gap-6 '>
          <ClinicLocationHeader
            clinicName={clinicName}
            title={'Contact Clinic'}
            trialLocationImageId={trialLocationImageId}
          />

          {usePlainPatientCallToSiteTreatment && <PhoneIcon />}

          <h3 className={`text-2xl font-extrabold leading-[130%] md:text-3xl`}>
            You might be a match, now call the clinic!
          </h3>
          {!usePlainPatientCallToSiteTreatment && (
            <div className='flex flex-col gap-4'>
              <div className='flex flex-row items-center gap-4'>
                <div className='flex size-8 flex-none items-center justify-center rounded-md bg-primary50'>
                  <Icon className='size-4 text-primary600' icon={faVolume} />
                </div>
                <p className='text-base'>Please make sure your volume is on</p>
              </div>
            </div>
          )}

          {usePlainPatientCallToSiteTreatment && <StudyIsReadyToCallYou />}

          <PatientCallScript
            conditionName={conditionName}
            idToken={idToken}
            isExpanded={isExpanded}
            mostQualifiedTrialApplicationIdToken={
              mostQualifiedTrialApplicationIdToken
            }
            patientName={patientName}
            setIsExpanded={setIsExpanded}
          />

          {/* Button to show for desktop */}
          <div className='hidden items-center justify-start md:flex'>
            <Button
              className={classMerge(
                buttonClassNames.primary.black,
                'pointer-events-auto flex w-56 rounded-full text-lg leading-[150%]',
              )}
              onClick={
                microphoneAccessGranted ? onStartCall : requestMicrophoneAccess
              }
            >
              Call Study Team
              <Icon className={'ml-2'} icon={faArrowRight} size='xs' />
            </Button>
          </div>
        </div>

        {!usePlainPatientCallToSiteTreatment && (
          <div className='flex w-[300px] flex-none flex-col items-center gap-4 md:gap-3'>
            <CircularTimer
              durationSeconds={120}
              size={240}
              strokeWidth={30}
              textClassName='text-3xl font-extrabold text-black'
            />
            <p className='text-center text-base font-bold text-neutral900 md:text-lg'>
              Call the clinic within 2 minutes for the best chance to connect.
            </p>
          </div>
        )}

        {/* Skip button to show for mobile - gradient is transparent at the top */}
        <div className='fixed inset-x-0 bottom-0 z-10 mx-auto flex max-w-4xl flex-row justify-center gap-4 bg-gradient-to-t from-primary50 via-[rgba(244,240,255,0.98),90%] to-primary50/0 p-4 md:hidden'>
          <div className='flex w-full flex-col items-center justify-center gap-2'>
            {telUriClicked && (
              <Button
                className={classMerge(
                  buttonClassNames.text.black,
                  'h-fit p-1 text-lg font-medium leading-[150%] text-neutral400 hover:text-neutral500',
                )}
                onClick={() => {
                  handleNextButtonClick()
                }}
              >
                Skip
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const PhoneIcon = () => {
  return (
    <div className='flex w-full flex-col items-center gap-4'>
      <div className='flex size-20 items-center justify-center rounded-full bg-primary50'>
        <Icon className='size-10 text-primary600' icon={faPhoneRegular} />
      </div>
    </div>
  )
}

const StudyIsReadyToCallYou = () => {
  return (
    <div className='flex flex-col items-start gap-4'>
      <p className='text-base leading-[150%] text-neutral600'>
        The study team is available and would love to hear from you - give them
        a call!
      </p>
    </div>
  )
}

const PatientCallScript = ({
  conditionName,
  idToken,
  isExpanded,
  mostQualifiedTrialApplicationIdToken,
  patientName,
  setIsExpanded,
}: {
  conditionName?: string
  idToken: string
  isExpanded: boolean
  mostQualifiedTrialApplicationIdToken: string
  patientName?: string | null
  setIsExpanded: (isExpanded: boolean) => void
}) => {
  return (
    <div className='flex flex-col items-start gap-4'>
      <Button
        className={classMerge(
          buttonClassNames.text.black,
          'flex h-fit cursor-pointer flex-row items-center gap-2 p-0',
        )}
        onClick={() => {
          const expandedEvent = isExpanded
            ? TrackingEvents.PATIENT_CALL_TO_SITE_SCRIPT_COLLAPSED
            : TrackingEvents.PATIENT_CALL_TO_SITE_SCRIPT_EXPANDED
          trackEvent(expandedEvent, {
            mostQualifiedTrialApplicationIdToken,
            trialApplicationIdToken: idToken,
          })
          setIsExpanded(!isExpanded)
        }}
      >
        <p className='text-base font-bold leading-[150%] text-neutral600'>
          Not sure what to say?
        </p>
        <Icon
          className={classMerge(
            'text-neutral600',
            isExpanded ? 'rotate-180' : '',
            'transform transition-all duration-300',
          )}
          icon={faChevronDown}
        />
      </Button>
      {isExpanded && (
        <div className='flex flex-col gap-2'>
          <p className='text-base font-medium leading-[150%] text-black'>
            Hi, my name is {patientName}. I just submitted an application on
            Power for a {conditionName} study at your clinic.
          </p>
          <p className='text-base font-medium leading-[150%] text-black'>
            I’m calling to discuss my application to see if I’m a candidate for
            a screening visit.
          </p>
        </div>
      )}
    </div>
  )
}

const MobileView = (props: PatientCallToSiteIntroVariantProps) => {
  const { handleTelUriClick, requestMicrophoneAccess, telUriClicked } = props
  return (
    <div className='block md:hidden'>
      <YouAreAMatch
        {...props}
        handleTelUriClick={handleTelUriClick}
        requestMicrophoneAccess={requestMicrophoneAccess}
        telUriClicked={telUriClicked}
      />
    </div>
  )
}

const YouAreAMatch = (
  props: PatientCallToSiteIntroProps & {
    handleTelUriClick: () => void
    requestMicrophoneAccess: () => void
    telUriClicked: boolean
  },
) => {
  const {
    clinicalLocationRelayPhoneNumberAliases,
    conditionName,
    handleNextButtonClick,
    handleTelUriClick,
    idToken,
    microphoneAccessGranted,
    mostQualifiedTrialApplicationIdToken,
    onStartCall,
    patientName,
    requestMicrophoneAccess,
    telUriClicked,
    usePlainPatientCallToSiteTreatment,
  } = props

  const containerRef = useRef<HTMLInputElement | null>(null)
  const [size, setSize] = useState<number | null>(null)
  const [strokeWidth, setStrokeWidth] = useState<number | null>(null)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const { width } = entry.contentRect
        setSize(width >= 768 ? 100 : 80) // desktop size is 100, mobile size is 80
        setStrokeWidth(width >= 768 ? 14 : 12) // desktop stroke width is 14, mobile stroke width is 12
      }
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return (
    <div className='flex flex-col justify-between md:mt-4' ref={containerRef}>
      <div className='mx-auto mb-48 grid max-w-2xl grid-cols-1 items-center gap-4 overflow-y-auto md:gap-y-6'>
        {usePlainPatientCallToSiteTreatment && <PhoneIcon />}

        <h3 className={`text-2xl font-extrabold leading-[130%] md:text-4xl`}>
          You might be a match, now call the clinic!
        </h3>

        {usePlainPatientCallToSiteTreatment && <StudyIsReadyToCallYou />}

        {!usePlainPatientCallToSiteTreatment && (
          <div className='flex flex-row items-center gap-4 md:gap-3'>
            {size && strokeWidth && (
              <CircularTimer
                durationSeconds={120}
                size={size}
                strokeWidth={strokeWidth}
              />
            )}
            <p className='text-base font-bold leading-[150%] text-neutral900 md:text-lg'>
              Call the clinic within 2 minutes for the best chance to connect.
            </p>
          </div>
        )}

        {/* Button to show for mobile */}
        {clinicalLocationRelayPhoneNumberAliases && (
          <a
            className={classMerge(
              buttonClassNames.primary.black,
              'pointer-events-auto flex w-full rounded-full text-lg font-bold leading-[150%] md:hidden',
            )}
            href={clinicalLocationRelayPhoneNumberAliases.phoneNumberTelUri}
            onClick={handleTelUriClick}
          >
            <Icon className={'mr-2'} icon={faPhone} />
            {telUriClicked ? 'Try calling again' : 'Call Study Team'}
          </a>
        )}

        <PatientCallScript
          conditionName={conditionName}
          idToken={idToken}
          isExpanded={isExpanded}
          mostQualifiedTrialApplicationIdToken={
            mostQualifiedTrialApplicationIdToken
          }
          patientName={patientName}
          setIsExpanded={setIsExpanded}
        />

        {/* Button to show for desktop */}
        <div className='hidden justify-center md:flex'>
          <Button
            className={classMerge(
              buttonClassNames.primary.black,
              'pointer-events-auto flex w-full rounded-full text-lg font-bold leading-[150%]',
            )}
            onClick={
              microphoneAccessGranted ? onStartCall : requestMicrophoneAccess
            }
          >
            <Icon className={'mr-2'} icon={faPhone} />
            Call Study Team
          </Button>
        </div>
      </div>
      {/* Skip button to show for mobile - gradient is transparent at the top */}
      <div className='fixed inset-x-0 bottom-0 z-10 mx-auto flex max-w-4xl flex-row justify-center gap-4 bg-gradient-to-t from-primary50 via-[rgba(244,240,255,0.98),90%] to-primary50/0 p-4 md:hidden'>
        <div className='flex w-full flex-col items-center justify-center gap-2'>
          {telUriClicked && (
            <Button
              className={classMerge(
                buttonClassNames.text.black,
                'h-fit p-1 text-lg font-medium leading-[150%] text-neutral400 hover:text-neutral500',
              )}
              onClick={() => {
                handleNextButtonClick()
              }}
            >
              Skip
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

const MicrophonePermissionModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: (flag: boolean) => void
}) => {
  return (
    <Modal
      className='my-[60%] md:my-[30%] lg:my-[20%]'
      onClose={() => onClose(false)}
      visible={isOpen}
    >
      <div
        className='flex cursor-pointer flex-col items-start gap-8'
        onClick={() => onClose(false)}
      >
        <Icon
          className='size-16 text-primary500'
          icon={faHandPointUp}
          size='2x'
        />
        <div>
          <Text className={`text-xl font-bold md:text-3xl`} styleName={'h3'}>
            Click Allow
          </Text>
          <Text className='text-neutral600'>
            Click allow on the browser pop-up to ensure your microphone is
            enabled for a live call.
          </Text>
        </div>
      </div>
    </Modal>
  )
}

const ChangeBrowserModal = ({
  isOpen,
  onClick,
  onClose,
  userAgent,
}: {
  isOpen: boolean
  onClick: () => void
  onClose: (flag: boolean) => void
  userAgent: string
}) => {
  const browserName = isIos(userAgent) ? 'Safari' : 'Chrome'
  return (
    <Modal
      bodyClassName='pb-6'
      className='my-[40%] md:my-[30%] lg:my-[20%]'
      onClose={() => onClose(false)}
      visible={isOpen}
    >
      <div className='mx-4 flex cursor-pointer flex-col items-start gap-6'>
        <Icon className='size-16 text-primary500' icon={faGlobe} size='2x' />
        <div className='flex flex-col gap-2'>
          <Text className={`text-2xl font-bold md:text-3xl`} styleName={'h3'}>
            Switch to {browserName} to call this clinic
          </Text>
          <Text className='text-neutral600'>
            You’re currently in Facebook’s web browser. To call the clinic, we
            need to switch to {browserName}.
          </Text>
        </div>
        <div className='w-full'>
          <Button
            className={classMerge(
              buttonClassNames.legacy.fill.black,
              'w-full rounded-lg',
            )}
            onClick={onClick}
          >
            Open in {browserName}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const DirectCallSiteOnMobileModal = ({
  clinicName,
  isOpen,
  onClose,
}: {
  clinicName: string
  isOpen: boolean
  onClose: (flag: boolean) => void
}) => {
  return (
    <Modal
      bodyClassName='pb-4'
      className='my-[40%]'
      onClose={() => onClose(false)}
      visible={isOpen}
    >
      <div
        className='flex cursor-pointer flex-col items-start gap-8 p-4'
        onClick={() => onClose(false)}
      >
        <Icon className='size-10 text-primary500' icon={faPhone} />
        <div className='flex flex-col gap-4'>
          <p className='text-lg font-medium leading-[150%] text-neutral600'>
            {`${clinicName} would love to hear from you now!`}
          </p>
          <div className='flex flex-row items-center justify-center gap-2'>
            <Icon className='text-neutral900' icon={faArrowDown} />
            <p className='text-lg font-bold leading-[150%] text-neutral900'>
              Click Call below
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}
